import type { ComponentProps, CSSProperties, ReactNode } from 'react';
import { Link, type LinkProps } from '@tanstack/react-router';
import { useId, useRef } from 'react';
import { cn } from '@worldcoo/ui/lib/utils';
import { useInView } from '@worldcoo/ui/hooks/use-in-view';
import { ArrowUpRight, Heart, ImageIcon } from 'lucide-react';

/** The badge must always be SOLID and contrast with the pill, never translucent: it is what drifts. */
const PILL_SURFACE = {
	brand: 'bg-brand hover:bg-brand-strong text-black',
	ink: 'bg-ink text-white hover:opacity-90',
	white: 'bg-white text-black hover:opacity-90',
	outline: 'border-ink hover:bg-ink/5 text-ink border bg-white',
} as const;

const PILL_BADGE = {
	brand: 'bg-ink text-white',
	ink: 'text-ink bg-white',
	white: 'bg-ink text-white',
	outline: 'bg-ink text-white',
} as const;

type PillVariant = keyof typeof PILL_SURFACE;

function pillClassName(variant: PillVariant, className?: string): string {
	return cn(
		'flex w-fit items-center gap-2 rounded-full py-2.5 pr-2.5 pl-4 text-sm font-medium transition-colors',
		PILL_SURFACE[variant],
		className,
	);
}

function PillBadge({ variant }: { variant: PillVariant }) {
	return (
		<span className={cn('grid size-6 shrink-0 place-items-center rounded-full', PILL_BADGE[variant])}>
			<ArrowUpRight className="size-3.5" />
		</span>
	);
}

function PillBody({ variant, children }: { variant: PillVariant; children: ReactNode }) {
	return (
		<>
			{children}
			<PillBadge variant={variant} />
		</>
	);
}

/** Button's own `hover:bg-*` does not merge with the pill's `bg-*`, so it would hover backoffice green. */
const PILL_SUBMIT_HOVER = {
	brand: 'hover:bg-brand-strong',
	ink: 'hover:bg-ink',
	white: 'hover:bg-white',
	outline: 'hover:bg-white',
} as const;

/** The pill for the shared `ContactForm`, which renders its own submit button. */
export function pillSubmit(
	label: string,
	options?: { variant?: PillVariant; className?: string },
): { label: string; className: string; adornment: ReactNode } {
	const variant = options?.variant ?? 'white';
	return {
		label,
		// `h-auto` beats the Button variant's height, which is sized for the backoffice.
		className: pillClassName(variant, cn('h-auto cursor-pointer', PILL_SUBMIT_HOVER[variant], options?.className)),
		adornment: <PillBadge variant={variant} />,
	};
}

export function PillLink({
	variant = 'brand',
	...props
}: {
	/** Mutually exclusive with `href`. */
	to?: LinkProps['to'];
	href?: string;
	children: ReactNode;
	variant?: PillVariant;
	className?: string;
}) {
	const className = pillClassName(variant, props.className);
	const body = <PillBody variant={variant}>{props.children}</PillBody>;

	if (props.to) {
		return (
			<Link to={props.to} className={className}>
				{body}
			</Link>
		);
	}
	return (
		<a href={props.href ?? '#'} className={className}>
			{body}
		</a>
	);
}

/** Spreads the rest of the props so Radix `asChild` triggers can hand it their handlers and ref. */
export function PillButton({
	variant = 'brand',
	className,
	children,
	...props
}: ComponentProps<'button'> & { variant?: PillVariant }) {
	return (
		<button type="button" className={cn(pillClassName(variant, className), 'cursor-pointer')} {...props}>
			<PillBody variant={variant}>{children}</PillBody>
		</button>
	);
}

export function Eyebrow({ children, icon, className }: { children: ReactNode; icon?: ReactNode; className?: string }) {
	return (
		<span className={cn('text-ink flex items-center gap-2 text-sm font-semibold tracking-wide uppercase', className)}>
			{icon ?? <Heart className="text-brand-strong size-4 fill-current" />}
			{children}
		</span>
	);
}

/**
 * Figma builds every dotted area from the same 777x501 ellipse; the geometry is the design's CSS export,
 * not the eye. Glows UNION. `strength` is the centre alpha, measured off the design because Figma's
 * compositing does not map one-for-one.
 */
export function diamondGlows(...centres: { x: string; y: string; strength?: number }[]): string {
	return centres
		.map(
			({ x, y, strength = 0.4 }) =>
				`radial-gradient(388.5px 250.5px at ${x} ${y}, rgba(0,0,0,${strength}), transparent)`,
		)
		.join(', ');
}

const NO_MASKS: string[] = [];

/**
 * An SVG tile masking a `background-color`: a data-URI SVG cannot read a CSS custom property, and a
 * gradient only draws round marks. `masks` INTERSECT, one per nesting level, because masks on one
 * element union and `mask-composite: intersect` still differs between engines. At least one is required.
 */
export function DiamondGrid({
	className,
	color = 'var(--color-brand)',
	size = 5,
	gap = 27,
	masks = NO_MASKS,
	opacity = 0.45,
}: {
	className?: string;
	color?: string;
	/** Measured off the Figma export (5px on a 27px pitch, orange at ~0.46 alpha): re-measure, don't nudge. */
	size?: number;
	/** The pitch, in px. */
	gap?: number;
	masks?: string[];
	opacity?: number;
}) {
	const half = size / 2;
	const centre = gap / 2;
	const svg =
		`<svg xmlns="http://www.w3.org/2000/svg" width="${gap}" height="${gap}" viewBox="0 0 ${gap} ${gap}">` +
		`<path d="M${centre} ${centre - half}L${centre + half} ${centre}L${centre} ${centre + half}L${centre - half} ${centre}Z" fill="#fff"/></svg>`;
	const shape = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;

	const tile: CSSProperties = {
		backgroundColor: color,
		maskImage: shape,
		WebkitMaskImage: shape,
		maskSize: `${gap}px ${gap}px`,
		WebkitMaskSize: `${gap}px ${gap}px`,
		maskRepeat: 'repeat',
		WebkitMaskRepeat: 'repeat',
	};

	let node = <div className="absolute inset-0" style={tile} />;

	for (const mask of [...masks].reverse()) {
		node = (
			<div className="absolute inset-0" style={{ maskImage: mask, WebkitMaskImage: mask }}>
				{node}
			</div>
		);
	}

	return (
		<div aria-hidden className={cn('pointer-events-none absolute inset-0', className)} style={{ opacity }}>
			{node}
		</div>
	);
}

export function Squiggle({
	children,
	className,
	lineClassName,
	animate = true,
}: {
	children: ReactNode;
	className?: string;
	lineClassName?: string;
	/** Off only where the line is not a heading flourish. */
	animate?: boolean;
}) {
	return (
		<span className={cn('relative inline-block whitespace-nowrap', className)}>
			{children}
			<SquiggleLine className={lineClassName} animate={animate} />
		</span>
	);
}

/** The exact hand-drawn scribble exported from Figma (Website Hand-Off). */
const SQUIGGLE_PATH =
	'M7.50694 8.04188C10.2159 8.04188 13.0539 7.95811 15.7629 7.87434C16.9239 7.87434 17.9558 7.79058 19.1168 7.79058C24.0188 7.62304 28.9207 7.4555 33.8227 7.28796C39.7567 7.12042 45.5616 6.86911 51.4956 6.70157C59.8805 6.36649 68.2654 6.11518 76.6503 5.7801C78.5853 5.69633 80.5203 5.69633 82.4553 5.61257C87.3572 5.44503 92.2592 5.27749 97.1612 5.19372C102.063 5.02618 106.965 4.85864 111.867 4.77487C113.802 4.6911 115.737 4.60733 117.672 4.60733C125.412 4.43979 133.281 4.27225 141.021 4.10471C145.794 4.02094 150.567 3.93717 155.469 3.76963C157.404 3.76963 159.21 3.68586 161.145 3.68586C168.627 3.60209 176.237 3.51832 183.719 3.43455C191.201 3.35079 198.554 3.26702 206.036 3.18325C207.971 3.18325 209.906 3.18325 211.97 3.18325C216.872 3.18325 221.645 3.18325 226.547 3.18325C234.158 3.18325 241.64 3.18325 249.251 3.09948C251.702 3.09948 254.153 3.09948 256.604 3.09948C261.764 3.09948 266.924 3.09948 272.084 3.09948C272.471 3.09948 272.987 3.09948 273.374 3.09948C261.764 3.18325 250.025 3.26702 238.415 3.43455C233.513 3.51832 228.74 3.51832 223.838 3.60209C221.774 3.60209 219.581 3.60209 217.517 3.68586C210.422 3.76963 203.456 3.93717 196.361 4.02094C188.234 4.18848 180.107 4.27225 171.98 4.43979C170.562 4.43979 169.272 4.52356 167.853 4.52356C163.467 4.6911 159.21 4.77487 154.824 4.94241C146.052 5.19372 137.28 5.44503 128.508 5.69633C127.218 5.69633 125.928 5.7801 124.638 5.86387C120.381 6.03141 115.995 6.28272 111.738 6.45026C103.998 6.78534 96.3872 7.12042 88.6472 7.4555C86.5832 7.53927 84.3903 7.70681 82.3263 7.79058C77.4243 8.04188 72.5224 8.29319 67.6204 8.62827C61.4285 8.96335 55.1075 9.29843 48.9156 9.63351C40.2727 10.1361 31.5007 10.6387 22.8578 11.1414C20.7938 11.2251 18.7298 11.3927 16.5369 11.4764C12.7959 11.7277 9.05492 11.9791 5.31396 12.2304C4.92696 12.2304 4.53996 12.3979 4.53996 12.733C4.53996 12.9843 4.92696 13.2356 5.31396 13.2356C6.73294 13.2356 8.02293 13.3194 9.44192 13.3194C9.18392 13.7382 9.05492 13.9895 9.05492 14.2408C9.05492 15.1623 10.2159 16 11.7639 16C22.4708 15.5812 33.0487 15.0785 43.7556 14.7435C53.0435 14.4921 62.3315 14.1571 71.6194 13.9058C81.8103 13.5707 92.1302 13.2356 102.321 12.9843C105.546 12.9005 108.771 12.8168 111.996 12.6492C112.899 12.6492 113.802 12.5654 114.834 12.5654C131.346 12.3141 147.858 11.9791 164.37 11.7277C173.141 11.5602 182.042 11.3927 190.814 11.3089C194.039 11.2251 197.135 11.2251 200.36 11.1414C217.259 10.9738 234.158 10.8063 251.057 10.6387C258.281 10.555 265.505 10.4712 272.729 10.3874C278.921 10.3037 285.113 10.3037 291.304 10.1361C296.464 10.0524 301.495 9.88482 306.655 9.80105C309.235 9.71728 311.815 9.71728 314.266 9.63351C320.071 9.3822 325.876 9.13089 331.681 8.87958C331.294 9.21466 331.294 9.71728 331.423 10.0524C331.552 10.4712 331.939 10.8063 332.584 10.9738C333.1 11.1414 333.874 11.3089 334.39 11.1414C335.68 10.8063 336.97 10.4712 338.131 10.1361C338.131 10.1361 338.131 10.1361 338.002 10.1361C338.131 10.1361 338.131 10.0524 338.26 10.0524C338.389 10.0524 338.518 9.96859 338.518 9.96859H338.389C339.034 9.80105 339.679 9.63351 340.453 9.3822C341.098 9.21466 341.872 8.96335 342.517 8.79581C343.291 8.5445 343.936 8.29319 344.71 8.04188C345.484 7.79058 346 7.12042 346 6.53403C346 6.19895 345.871 5.94764 345.613 5.61257C345.355 5.27749 344.71 4.85864 344.065 4.77487C343.42 4.6911 342.775 4.60733 342.13 4.60733C342.001 4.60733 341.872 4.60733 341.743 4.60733C341.356 4.60733 340.84 4.60733 340.453 4.6911C339.421 4.77487 338.518 4.85864 337.486 4.85864C336.712 4.85864 335.938 4.94241 335.035 4.94241C332.842 5.02618 330.778 5.10995 328.585 5.27749C328.069 5.27749 327.424 5.36126 326.908 5.36126C327.166 5.19372 327.295 5.02618 327.295 4.85864C327.424 4.6911 327.424 4.52356 327.424 4.35602C327.424 4.27225 327.424 4.10471 327.553 4.02094C327.553 3.8534 327.553 3.68586 327.424 3.60209C327.424 3.60209 327.553 3.60209 327.553 3.51832C327.94 3.35079 328.327 3.18325 328.585 2.84817C328.843 2.59686 328.972 2.26178 328.972 1.9267C328.972 1.59162 328.843 1.34031 328.585 1.00524C328.456 0.921466 328.327 0.753927 328.198 0.670157C327.811 0.418848 327.424 0.335079 327.037 0.251309C326.263 0.0837696 325.36 0 324.457 0C323.683 0 323.038 0 322.264 0C321.232 0 320.2 0 319.168 0C317.749 0 316.201 0 314.782 0C310.912 0 307.042 0 303.172 0C299.56 0 295.819 0 292.207 0C288.853 0 285.629 0 282.275 0C268.988 0 255.83 0.0837696 242.543 0.167539C232.61 0.251309 222.677 0.335079 212.744 0.335079C208.358 0.335079 203.843 0.418848 199.457 0.502618C189.524 0.670157 179.591 0.753927 169.659 0.921466C166.821 0.921466 163.983 1.00524 161.145 1.00524C159.468 1.00524 157.92 1.08901 156.243 1.08901C146.439 1.34031 136.635 1.59162 126.831 1.84293C123.864 1.9267 120.897 2.01047 117.801 2.09424C116.124 2.09424 114.447 2.17801 112.77 2.26178C102.966 2.59686 93.2912 2.93194 83.4873 3.26702C78.4563 3.43456 73.4254 3.60209 68.3944 3.8534C59.7515 4.18848 51.1086 4.52356 42.5946 4.94241C35.1127 5.27749 27.6308 5.5288 20.1488 5.7801C18.9878 5.86387 17.8268 5.86387 16.5369 5.94764C14.6019 6.03141 12.6669 6.03141 10.7319 6.11518C9.05492 6.45026 7.24894 6.45026 5.57195 6.45026C5.44296 6.11518 4.92696 5.86387 4.53996 5.94764C3.37897 5.94764 2.34698 6.03141 1.18599 6.11518C0.669996 6.19895 0.154001 6.36649 0.0250019 6.70157C-0.103997 7.12042 0.283 7.53927 0.798995 7.62304C1.31499 7.70681 1.83099 7.79058 2.34698 7.87434C2.86298 7.95811 3.24997 7.95812 3.76597 7.95812C5.05596 8.04188 6.21695 8.04188 7.50694 8.04188ZM309.364 5.36126C310.912 5.36126 312.589 5.36126 314.137 5.36126C314.266 5.61257 314.524 5.7801 314.782 5.94764C314.137 5.94764 313.492 6.03141 312.976 6.03141C311.815 6.03141 310.654 6.11518 309.493 6.11518C304.333 6.19895 299.302 6.36649 294.142 6.45026C291.82 6.53403 289.498 6.6178 287.177 6.6178C283.565 6.6178 279.824 6.70157 276.212 6.70157C267.827 6.78534 259.571 6.86911 251.186 6.95288C234.932 7.12042 218.807 7.28796 202.553 7.4555C190.943 7.53927 179.333 7.79058 167.724 8.04188C150.309 8.37696 132.765 8.62827 115.35 8.96335C111.867 9.04712 108.384 9.13089 104.901 9.29843C94.9682 9.63351 85.0353 9.88482 75.1023 10.2199C65.4274 10.555 55.6235 10.8063 45.9486 11.1414C45.0456 11.1414 44.1426 11.2251 43.2396 11.2251C45.8196 11.0576 48.5286 10.9738 51.1086 10.8063C60.2675 10.3874 69.4264 9.88482 78.5853 9.46597C81.8103 9.29843 85.0353 9.13089 88.1312 8.96335C89.8082 8.87958 91.4852 8.79581 93.0332 8.79581C102.708 8.46073 112.254 8.04188 121.8 7.70681C123.606 7.62304 125.541 7.53927 127.347 7.4555C130.056 7.37173 132.765 7.28796 135.345 7.28796C145.149 7.03665 154.953 6.78534 164.757 6.53403C166.95 6.45026 169.143 6.45026 171.206 6.36649C171.723 6.36649 172.367 6.36649 172.883 6.36649C174.431 6.36649 175.979 6.36649 177.398 6.28272C187.202 6.11518 197.135 6.03141 206.939 5.86387C211.454 5.7801 215.969 5.69633 220.484 5.69633C235.061 5.61257 249.767 5.5288 264.344 5.44503C279.566 5.44503 294.529 5.36126 309.364 5.36126Z';

/** The same drawing as `SQUIGGLE_PATH`, as a hand traces it: docs/notes/portals/hand-drawn-strokes.md */
const PEN_ROUTE = [
	// Pass 1, left to right along the top into the hook.
	'M0 7 L40 6 L80 4.5 L120 3.4 L160 2.5 L200 2 L240 1.7 L280 1.5 L305 1.5 L318 1.6 L325 1.9 L327 2.8',
	// Pass 2, back to the left, descending.
	'L322 4.3 L300 4.2 L270 4.3 L230 4.6 L190 5.1 L150 6 L110 7.4 L75 8.9 L45 10.6 L22 11.9 L6 13 L3 14.3',
	// Pass 3, left to right along the bottom.
	'L20 14.4 L50 13.2 L85 11.8 L120 10.8 L160 9.9 L200 9.3 L240 8.8 L280 8.5 L312 8.4 L322 9',
	// The flourish: out along the tail's top, back along its bottom, 3.6 apart so no seam shows.
	'L327 7.4 L332 6 L338 5.9 L343 6 L345 6.6 L342 8.6 L337 9.7 L332 9.8 L329 8.8',
].join(' ');

/** 4.2 left slivers of the artwork uncovered; much past 5 a pass uncovers its neighbours ~3.5 away. */
const PEN_WIDTH = 5;

/**
 * The pen is rendered and the artwork is its clip, because Chrome never repaints a `<mask>` whose
 * contents a WAAPI animation drives: docs/notes/portals/hand-drawn-strokes.md
 */
export function SquiggleLine({
	className,
	animate = false,
}: {
	className?: string;
	/** For a line that appears on its own, like a heading's. */
	animate?: boolean;
}) {
	// `useId` is SSR-stable; the characters `url(#…)` finds awkward come out.
	const clipId = `squiggle-pen-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
	const frame = useRef<SVGSVGElement>(null);
	// `false` on the server and the first client render alike, so hydration sees one answer.
	const inView = useInView(frame);
	// The nib is never swapped for the export at the end, or the drawing looks corrected once finished.
	const drawing = animate;

	return (
		<svg
			ref={frame}
			className={cn('text-brand absolute -bottom-[0.15em] left-0 h-[0.32em] w-full', className)}
			viewBox="0 0 346 16"
			preserveAspectRatio="none"
			fill="none"
			aria-hidden
		>
			{drawing ? (
				<>
					<clipPath id={clipId}>
						<path d={SQUIGGLE_PATH} />
					</clipPath>
					<path
						d={PEN_ROUTE}
						clipPath={`url(#${clipId})`}
						fill="none"
						stroke="currentColor"
						strokeWidth={PEN_WIDTH}
						strokeLinecap="round"
						strokeLinejoin="round"
						// One unit long, so a dash of `1` covers the route whatever its real length.
						pathLength={1}
						strokeDasharray="1 1"
						// Reduced motion is settled in CSS: reading the query in JS would hand hydration two pictures.
						className="transition-[stroke-dashoffset] duration-[1400ms] ease-[cubic-bezier(0.35,0,0.25,1)] motion-reduce:transition-none motion-reduce:[stroke-dasharray:none]!"
						style={{ strokeDashoffset: inView ? 0 : 1 }}
					/>
				</>
			) : (
				<path d={SQUIGGLE_PATH} fill="currentColor" />
			)}
		</svg>
	);
}

/** One tapered sweep (Figma hand-off, 182x7): the scribble's four passes turn to mud at nav size. */
const NAV_STROKE_PATH =
	'M181.204 3.87657C180.287 3.75727 179.443 3.7192 178.523 3.67767C177.603 3.63614 176.762 3.52029 175.845 3.40098C173.858 3.15545 171.951 2.83559 169.964 2.59005C166.22 2.10935 162.399 1.62519 158.644 1.37784C155.199 1.06654 151.674 0.829574 148.223 0.673846C147.302 0.632318 146.382 0.590791 145.538 0.552724C142.083 0.474777 138.632 0.319048 135.176 0.241101C127.112 0.11108 118.971 -0.0224023 110.9 0.00313943C102.982 0.0356026 95.0644 0.0680655 87.2198 0.181771C83.3741 0.242084 79.4518 0.298937 75.6062 0.35925C71.3701 0.557824 67.1409 0.600834 62.985 0.725086C54.2861 1.03407 45.6674 1.26873 36.9686 1.57772C29.4237 1.86083 21.945 2.38074 14.4558 3.13401C9.66523 3.69726 4.79441 4.33484 0.00029818 4.97588C5.42858 5.61049 10.9441 6.01522 16.4631 6.34217C20.0681 6.50482 23.6766 6.58969 27.2084 6.67109C29.5129 6.69713 31.821 6.64539 34.2058 6.5971C35.6666 6.58507 37.0543 6.4918 38.4419 6.39853C42.2179 6.17919 46.067 6.0411 49.8429 5.82176C54.2325 5.63011 58.5488 5.35721 62.9384 5.16556C67.4046 4.97737 71.9476 4.79264 76.4138 4.60445C80.4164 4.47327 84.4956 4.34556 88.5748 4.21785C92.5006 4.08321 96.4997 4.02982 100.422 3.97297C104.421 3.91958 108.343 3.86272 112.342 3.80933C116.418 3.7594 120.41 3.86157 124.482 3.88942C131.86 3.91052 139.227 4.16496 146.67 4.42286C150.812 4.60973 154.884 4.63758 159.033 4.66889C163.105 4.69674 167.178 4.72459 171.177 4.6712C172.791 4.66609 174.482 4.66444 176.1 4.58155C176.87 4.53838 177.717 4.49866 178.491 4.37771C179.189 4.25329 179.89 4.05109 180.583 4.00445C180.817 3.93705 180.974 3.86619 181.204 3.87657Z';

/** A butt-capped nib taller than the 7-unit artwork, wiped under its clip: docs/notes/portals/hand-drawn-strokes.md */
const NAV_STROKE_WIPE = 'M0 3.5H182';

/** Signed from CSS, as its cues are `:hover` and `aria-current`: the caller sets `stroke-dashoffset` to 0. */
export function NavStroke({ className, penClassName }: { className?: string; penClassName?: string }) {
	// `useId` is SSR-stable; the characters `url(#…)` finds awkward come out.
	const clipId = `nav-stroke-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

	return (
		<svg
			className={cn('text-brand absolute -bottom-[0.12em] left-0 h-[0.26em] w-full', className)}
			viewBox="0 0 182 7"
			preserveAspectRatio="none"
			fill="none"
			aria-hidden
		>
			<clipPath id={clipId}>
				<path d={NAV_STROKE_PATH} />
			</clipPath>
			<path
				d={NAV_STROKE_WIPE}
				clipPath={`url(#${clipId})`}
				stroke="currentColor"
				strokeWidth={8}
				pathLength={1}
				strokeDasharray="1 1"
				// Unsigned at rest, so reduced motion still gets the mark: it appears rather than draws.
				className={cn('[stroke-dashoffset:1]', penClassName)}
			/>
		</svg>
	);
}

/** For a Figma photo not exported yet (Figma MCP quota); swap for the real <Image> when it is. */
export function PhotoPlaceholder({ className, label }: { className?: string; label?: string }) {
	return (
		<div
			className={cn('bg-ink/10 text-ink/30 flex items-center justify-center overflow-hidden', className)}
			role="img"
			aria-label={label ?? 'Photo'}
		>
			<ImageIcon className="size-8" />
		</div>
	);
}

/**
 * Only the border and the message turn `alert`: the shared `destructive` ring and label are an alarm on
 * dark green. Each control is named by ELEMENT because a bare `[aria-invalid=true]` only TIES the
 * `aria-invalid:` variants it overrides. Plain literals, for Tailwind's scanner.
 */
export const FORM_ERROR_COLORS = [
	'[&_input[aria-invalid=true]]:border-alert [&_input[aria-invalid=true]]:ring-0',
	'[&_textarea[aria-invalid=true]]:border-alert [&_textarea[aria-invalid=true]]:ring-0',
	'[&_select[aria-invalid=true]]:border-alert [&_select[aria-invalid=true]]:ring-0',
	// The checkbox and the combobox trigger.
	'[&_button[aria-invalid=true]]:border-alert [&_button[aria-invalid=true]]:ring-0',
	'[&_[data-slot=form-message]]:text-alert',
	'[&_[data-slot=form-label][data-error=true]]:text-inherit',
].join(' ');

/**
 * Re-themes the LIGHT shared contact form from outside, so the footer band and the Contact panel keep
 * one form, one consent flow, one endpoint. Plain literals, for Tailwind's scanner.
 */
export const DARK_FORM_FIELDS = [
	'[&_button[role=checkbox]]:border-white/30 [&_button[role=checkbox]]:bg-white/5',
	'[&_button[role=combobox]]:border-white/10 [&_button[role=combobox]]:bg-white/5 [&_button[role=combobox]]:text-white',
	// The chevron ships `text-muted-foreground`, invisible here.
	'[&_select]:border-white/10 [&_select]:bg-white/5 [&_select]:text-white',
	'[&_[data-slot=native-select-icon]]:text-white/60',
	// The OS draws the options in the select's colour, so they would be white-on-white.
	'[&_option]:text-ink',
	'[&_input]:border-white/10 [&_input]:bg-white/5 [&_input]:text-white [&_input::placeholder]:text-white/40',
	'[&_label]:text-white',
	'[&_textarea]:border-white/10 [&_textarea]:bg-white/5 [&_textarea]:text-white [&_textarea::placeholder]:text-white/40',
	// Chrome's autofill UA rule beats `background-color`; #303A37 is `white/5` composited over `ink`.
	'[&_input:-webkit-autofill]:[box-shadow:inset_0_0_0_1000px_#303A37] [&_input:-webkit-autofill]:[-webkit-text-fill-color:#fff]',
	// Olive, as the shipped `text-primary` is backoffice green; `inline`, as `display: contents` paints no underline.
	'[&_[data-slot=dialog-trigger]]:text-olive [&_[data-slot=dialog-trigger]]:inline [&_[data-slot=dialog-trigger]]:underline',
	FORM_ERROR_COLORS,
].join(' ');
